<template>
  <div class="div-full-width">
    <el-date-picker
      v-if="isDesktop || forceDesktop"
      v-model="calVal"
      :disabled="disabled"
      unlink-panels
      type="daterange"
      range-separator="To"
      start-placeholder="Start date"
      end-placeholder="End date"
      value-format="YYYY-MM-DD"
      :size="size"
      :shortcuts="pickerOptions.shortcuts"
      :disabled-date="pickerOptions.disabledDate"
      @update:modelValue="update"
    />
    <div v-else>
      <el-date-picker
        v-model="dateFrom"
        type="date"
        value-format="YYYY-MM-DD"
        placeholder="Start date"
        :disabled="disabled"
      />
      <el-date-picker
        v-model="dateTo"
        class="mt-1"
        type="date"
        value-format="YYYY-MM-DD"
        placeholder="End date"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, defineComponent, watch } from 'vue'
import useResize from '@/hooks/useResize'
import moment from 'moment'

export default defineComponent({
  name: 'DateRangePicker',
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    forceDesktop: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'default'
    }
  },
  setup(props, context) {
    const calVal = ref<any>(props.modelValue)

    const update = (newValue: string[]) => {
      context.emit('update:modelValue', newValue)
    }

    const { isDesktop } = useResize()

    const dateFrom = computed({
      get: () => {
        return calVal.value[0]
      },
      set: (newVal) => {
        context.emit('update:modelValue', calVal)
      }
    })

    const dateTo = computed({
      get: () => {
        return calVal.value[1]
      },
      set: (newVal) => {
        context.emit('update:modelValue', calVal)
      }
    })

    const pickerOptions = {
      disabledDate: (time: Date) => {
        return time.getTime() > Date.now()
      },
      shortcuts: [
        {
          text: 'Today',
          value: () => {
            const today = moment().toDate()
            return [today, today]
          }
        },
        {
          text: 'Yesterday',
          value: () => {
            const yesterday = moment().subtract(1, 'days').toDate()
            return [yesterday, yesterday]
          }
        },
        {
          text: '7 days',
          value: () => {
            const start = moment().subtract(6, 'days').toDate()
            const end = moment().toDate()
            return [start, end]
          }
        },
        {
          text: 'This month',
          value: () => {
            const start = moment().startOf('month').toDate()
            const end = moment().endOf('month').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last month',
          value: () => {
            const start = moment()
              .subtract(1, 'month')
              .startOf('month')
              .toDate()
            const end = moment().subtract(1, 'month').endOf('month').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last 30 days',
          value: () => {
            const end = moment().endOf('day').toDate()
            const start = moment().subtract(30, 'days').startOf('day').toDate()
            return [start, end]
          }
        }
      ]
    }
    watch(
      () => props.modelValue,
      (newVal) => {
        calVal.value = newVal
      }
    )
    return { update, isDesktop, dateTo, dateFrom, calVal, pickerOptions }
  }
})
</script>

<style lang="scss" scoped>
:deep(.el-range-separator) {
  width: 10%;
}
</style>
